import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Menu, MessageCircle, X } from "lucide-react";
import { useLanguage } from "@/i18n";
import { BRAND, scrollToSection, whatsappLink } from "@/constants/brand";
import { Button } from "@/components/ui/button";

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <motion.header
      data-testid="navbar-container"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="glass fixed inset-x-0 top-0 z-50 h-20 border-b border-emerald-950/60"
    >
      <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          data-testid="navbar-brand"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.2 })}
          className="flex items-center gap-3"
        >
          <img
            src={BRAND.logo}
            alt="New Era"
            className="h-11 w-11 rounded-lg object-cover ring-1 ring-emerald-500/30"
          />
          <span className="font-display text-xl tracking-wide text-slate-100">
            New <span className="text-neon font-semibold">Era</span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          <button data-testid="nav-services-link" onClick={() => go("servicos")} className="text-sm text-slate-300 transition-colors hover:text-emerald-400">
            {t("nav.services")}
          </button>
          <button data-testid="nav-security-link" onClick={() => go("seguranca")} className="text-sm text-slate-300 transition-colors hover:text-emerald-400">
            {t("nav.security")}
          </button>
          <button data-testid="nav-schedule-cta" onClick={() => go("agendar")} className="text-sm text-slate-300 transition-colors hover:text-emerald-400">
            {t("nav.booking")}
          </button>
          <Link data-testid="admin-login-button" to="/admin" className="flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-emerald-400">
            <Lock className="h-3.5 w-3.5" />
            {t("nav.admin")}
          </Link>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <div className="flex items-center rounded-full border border-emerald-500/20 bg-black/40 p-1">
            <button
              data-testid="language-toggle-pt"
              onClick={() => setLang("pt")}
              className={`rounded-full px-3 py-1 font-tech text-xs transition-colors ${lang === "pt" ? "bg-emerald-500 text-[#030706]" : "text-slate-400 hover:text-slate-200"}`}
            >
              PT
            </button>
            <button
              data-testid="language-toggle-en"
              onClick={() => setLang("en")}
              className={`rounded-full px-3 py-1 font-tech text-xs transition-colors ${lang === "en" ? "bg-emerald-500 text-[#030706]" : "text-slate-400 hover:text-slate-200"}`}
            >
              EN
            </button>
          </div>
          <a data-testid="nav-whatsapp-cta" href={whatsappLink(t("hero.ctaWhatsapp"))} target="_blank" rel="noopener noreferrer">
            <Button size="sm" className="glow-button gap-2 border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20">
              <MessageCircle className="h-4 w-4" />
              {t("nav.whatsapp")}
            </Button>
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-emerald-500/20 text-slate-200 lg:hidden"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass border-b border-emerald-950/60 px-6 py-5 lg:hidden"
        >
          <div className="flex flex-col gap-4">
            <button data-testid="mobile-nav-services" onClick={() => go("servicos")} className="text-left text-sm text-slate-200">{t("nav.services")}</button>
            <button data-testid="mobile-nav-security" onClick={() => go("seguranca")} className="text-left text-sm text-slate-200">{t("nav.security")}</button>
            <button data-testid="mobile-nav-booking" onClick={() => go("agendar")} className="text-left text-sm text-slate-200">{t("nav.booking")}</button>
            <Link data-testid="mobile-admin-link" to="/admin" className="flex items-center gap-1.5 text-sm text-slate-300">
              <Lock className="h-3.5 w-3.5" /> {t("nav.admin")}
            </Link>
            <div className="flex items-center gap-2 pt-2">
              <button data-testid="mobile-language-toggle-pt" onClick={() => setLang("pt")} className={`rounded-full px-3 py-1 font-tech text-xs ${lang === "pt" ? "bg-emerald-500 text-[#030706]" : "border border-emerald-500/20 text-slate-400"}`}>PT</button>
              <button data-testid="mobile-language-toggle-en" onClick={() => setLang("en")} className={`rounded-full px-3 py-1 font-tech text-xs ${lang === "en" ? "bg-emerald-500 text-[#030706]" : "border border-emerald-500/20 text-slate-400"}`}>EN</button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
